import { Select } from '@/components/Controls/Select';
import { DateTimePicker } from '@/components/Custom/DateTimePicker';
import { Options } from '@/components/Controls/Select';
import { TFunction } from 'i18next';
import dayjs, { Dayjs } from 'dayjs';
import { NotFound } from '@/components/Message/NotFound';
import { Checkbox } from '@/components/Controls/Checkbox';

type StructureFilterProps = {
  t: TFunction<undefined>;
  isLoadingAllDistributors?: boolean;
  isLoadingAllGroups?: boolean;
  isLoadingAllCompanies?: boolean;
  isLoadingAllBranches?: boolean;
  groupOptions?: Options[];
  distributorOptions?: Options[];
  companyOptions?: Options[];
  branchOptions?: Options[];
  displayGroupFilter?: boolean;
  displayDistributorFilter?: boolean;
  displayCompanyFilter?: boolean;
  displayBranchFilter?: boolean;
  startDate?: string;
  endDate?: string;
  isWhiteMark?: string;
  distributorId?: string;
  groupId?: string;
  companyId?: string;
  branchId?: string;
  searchBeginDate?: (value: Dayjs) => void;
  searchEndDate?: (value: Dayjs) => void;
  searchWhiteMark?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  searchByDistributor?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  searchByGroup?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  searchByCompany?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  searchByBranch?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
};

const StructureFilter = ({
  t,
  isLoadingAllDistributors,
  isLoadingAllGroups,
  isLoadingAllCompanies,
  isLoadingAllBranches,
  groupOptions,
  distributorOptions,
  companyOptions,
  branchOptions,
  displayGroupFilter,
  displayDistributorFilter,
  displayBranchFilter,
  displayCompanyFilter,
  startDate,
  endDate,
  isWhiteMark,
  distributorId,
  groupId,
  companyId,
  branchId,
  searchBeginDate,
  searchEndDate,
  searchWhiteMark,
  searchByDistributor,
  searchByGroup,
  searchByCompany,
  searchByBranch,
}: StructureFilterProps) => {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-row gap-3 select-datepicker-container">
        <div className="flex flex-row gap-3">
          <label className="text-gray-600 text-sm self-center">
            {t('common:createdBetween')}
          </label>
          <DateTimePicker
            useTime={false}
            value={startDate ? dayjs(startDate) : null}
            onChange={searchBeginDate}
            disablePast={false}
            numberOfAllowedFutureDays={0}
            maxDate={endDate ? dayjs(endDate) : null}
          />
        </div>
        <div className="flex flex-row gap-3">
          <label className="text-gray-600 text-sm self-center">
            {t('common:and').toLowerCase()}
          </label>
          <DateTimePicker
            useTime={false}
            value={endDate ? dayjs(endDate) : null}
            onChange={searchEndDate}
            disablePast={false}
            numberOfAllowedFutureDays={0}
            minDate={startDate ? dayjs(startDate) : null}
          />
        </div>
      </div>
      <div>
        <Checkbox
          checkboxClassName="-mt-0.5 !w-[17.5px] !h-[17.5px]"
          textClassName="text-sm text-gray-600"
          text={t('common:onlyWhiteLabelCustomers')}
          checked={isWhiteMark === 'true'}
          onChange={searchWhiteMark}
        />
      </div>
      <div className="flex flex-col gap-3">
        {displayDistributorFilter && (
          <div className="flex items-center gap-3">
            <div className="flex flex-1 text-gray-600 text-sm">
              {`${t('common:linkedToDistributor')} :`}
            </div>
            <div className="w-[270px]">
              {isLoadingAllDistributors ? (
                <Select
                  selectClassName="h-[36px] bg-gray-FCFCFC dark-mode:bg-coal-500"
                  placeholder={t('common:allDistributors')}
                  options={[]}
                />
              ) : distributorOptions.length === 0 ? (
                <NotFound
                  containerClassName="justify-start gap-1.5"
                  iconClassName="-mt-1.5 text-lg"
                  textClassName="text-2sm"
                  text={t('common:noDistributorAvailable')}
                />
              ) : (
                <Select
                  selectClassName="h-[36px] bg-gray-FCFCFC dark-mode:bg-coal-500"
                  placeholder={t('common:allDistributors')}
                  options={distributorOptions}
                  value={distributorId || ''}
                  onChange={searchByDistributor}
                />
              )}
            </div>
          </div>
        )}
        {displayGroupFilter && (
          <div className="flex items-center gap-3">
            <div className="flex flex-1 text-gray-600 text-sm">
              {`${t('common:linkedToGroup')} :`}
            </div>
            <div className="w-[270px]">
              {isLoadingAllGroups ? (
                <Select
                  selectClassName="h-[36px] bg-gray-FCFCFC dark-mode:bg-coal-500"
                  placeholder={t('common:allGroups')}
                  options={[]}
                />
              ) : distributorId && groupOptions.length === 0 ? (
                <NotFound
                  containerClassName="justify-start gap-1.5"
                  iconClassName="-mt-1.5 text-lg"
                  textClassName="text-2sm"
                  text={t('common:noGroupLinkedToThisDistributor')}
                />
              ) : (
                <Select
                  selectClassName="h-[36px] bg-gray-FCFCFC dark-mode:bg-coal-500"
                  placeholder={t('common:allGroups')}
                  options={groupOptions}
                  value={groupId || ''}
                  onChange={searchByGroup}
                />
              )}
            </div>
          </div>
        )}
        {displayCompanyFilter && (
          <div className="flex items-center gap-3">
            <div className="flex flex-1 text-gray-600 text-sm">
              {`${t('common:linkedToCompany')} :`}
            </div>
            <div className="w-[270px]">
              {isLoadingAllCompanies ? (
                <Select
                  selectClassName="h-[36px] bg-gray-FCFCFC dark-mode:bg-coal-500"
                  placeholder={t('common:allCompanies')}
                  options={[]}
                />
              ) : groupId && companyOptions.length === 0 ? (
                <NotFound
                  containerClassName="justify-start gap-1.5"
                  iconClassName="-mt-1.5 text-lg"
                  textClassName="text-2sm"
                  text={t('common:noCompanyLinkedToThisGroup')}
                />
              ) : distributorId && companyOptions.length === 0 ? (
                <NotFound
                  containerClassName="justify-start gap-1.5"
                  iconClassName="-mt-1.5 text-lg"
                  textClassName="text-2sm"
                  text={t('common:noCompanyLinkedToThisDistributor')}
                />
              ) : companyOptions.length === 0 ? (
                <NotFound
                  containerClassName="justify-start gap-1.5"
                  iconClassName="-mt-1.5 text-lg"
                  textClassName="text-2sm"
                  text={t('common:youAreNotLinkedToAnyCompany')}
                />
              ) : (
                <Select
                  selectClassName="h-[36px] bg-gray-FCFCFC dark-mode:bg-coal-500"
                  placeholder={t('common:allCompanies')}
                  options={companyOptions}
                  value={companyId || ''}
                  onChange={searchByCompany}
                />
              )}
            </div>
          </div>
        )}
        {displayBranchFilter && (
          <div className="flex items-center gap-3">
            <div className="flex flex-1 text-gray-600 text-sm">
              {`${t('common:linkedToBranch')} :`}
            </div>
            <div className="w-[270px]">
              {isLoadingAllBranches ? (
                <Select
                  selectClassName="h-[36px] bg-gray-FCFCFC dark-mode:bg-coal-500"
                  placeholder={t('common:allBranches')}
                  options={[]}
                />
              ) : companyId && branchOptions.length === 0 ? (
                <NotFound
                  containerClassName="justify-start gap-1.5"
                  iconClassName="-mt-1.5 text-lg"
                  textClassName="text-2sm"
                  text={t('common:noBranchLinkedToThisCompany')}
                />
              ) : groupId && branchOptions.length === 0 ? (
                <NotFound
                  containerClassName="justify-start gap-1.5"
                  iconClassName="-mt-1.5 text-lg"
                  textClassName="text-2sm"
                  text={t('common:noBranchLinkedToThisGroup')}
                />
              ) : distributorId && branchOptions.length === 0 ? (
                <NotFound
                  containerClassName="justify-start gap-1.5"
                  iconClassName="-mt-1.5 text-lg"
                  textClassName="text-2sm"
                  text={t('common:noBranchLinkedToThisDistributor')}
                />
              ) : branchOptions.length === 0 ? (
                <NotFound
                  containerClassName="justify-start gap-1.5"
                  iconClassName="-mt-1.5 text-lg"
                  textClassName="text-2sm"
                  text={t('common:youAreNotLinkedToAnyBranch')}
                />
              ) : (
                <Select
                  selectClassName="h-[36px] bg-gray-FCFCFC dark-mode:bg-coal-500"
                  placeholder={t('common:allBranches')}
                  options={branchOptions}
                  value={branchId || ''}
                  onChange={searchByBranch}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StructureFilter;
